/**
 * Variant Price Display
 * Displays prices for each variant option in the variant picker
 */

class VariantPriceDisplay {
  constructor() {
    this.productData = null;
    this.init();
  }

  init() {
    // Wait for DOM to be ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.loadProductData());
    } else {
      this.loadProductData();
    }

    // Listen for variant changes or section updates
    document.addEventListener('variant:update', () => {
      setTimeout(() => this.setupPrices(), 100);
    });

    // Listen for Shopify section loads (theme editor)
    document.addEventListener('shopify:section:load', () => {
      this.loadProductData();
    });
  }

  /**
   * Load product data from Shopify product.js endpoint
   */
  async loadProductData() {
    // Get product handle from URL or meta tag
    const productHandle = this.getProductHandle();

    if (!productHandle) {
      console.warn('No product handle found');
      this.setupPrices();
      return;
    }

    try {
      const response = await fetch(`/products/${productHandle}.js`);
      if (response.ok) {
        this.productData = await response.json();
        this.setupPrices();
      }
    } catch (e) {
      console.warn('Failed to fetch product data:', e);
      this.setupPrices();
    }
  }

  /**
   * Get product handle from URL or meta tags
   * @returns {string|null} Product handle
   */
  getProductHandle() {
    // Try to get from URL first
    const pathParts = window.location.pathname.split('/');
    const productsIndex = pathParts.indexOf('products');

    if (productsIndex !== -1 && pathParts[productsIndex + 1]) {
      return pathParts[productsIndex + 1].split('?')[0];
    }

    // Try to get from variant picker
    const variantPicker = document.querySelector('variant-picker');
    if (variantPicker && variantPicker.dataset.productUrl) {
      const url = variantPicker.dataset.productUrl;
      const match = url.match(/\/products\/([^/?]+)/);
      if (match) {
        return match[1];
      }
    }

    return null;
  }

  /**
   * Format price from cents to currency string
   * @param {number} priceInCents - Price in cents
   * @returns {string} Formatted price
   */
  formatPrice(priceInCents) {
    const price = priceInCents / 100;

    // Get currency from Shopify.currency or default to USD
    const currency = window.Shopify?.currency?.active || 'USD';

    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(price);
  }


  /**
   * Setup prices for all variant options
   */
  setupPrices() {
    const variantPickers = document.querySelectorAll('variant-picker');

    variantPickers.forEach(picker => {
      const inputs = picker.querySelectorAll('input[type="radio"][data-variant-id]');

      inputs.forEach(input => {
        const variantId = parseInt(input.dataset.variantId);

        if (!variantId) return;

        // Find the label containing this input
        const label = input.closest('label');
        if (!label) return;

        // Remove existing price if any (for re-renders)
        const existingPrice = label.querySelector('.variant-price');
        if (existingPrice) {
          existingPrice.remove();
        }

        // Get variant price from product data
        this.addPriceToLabel(label, variantId);
      });
    });
  }

  /**
   * Add price to variant label
   * @param {HTMLElement} label - The label element
   * @param {number} variantId - The variant ID
   */
  addPriceToLabel(label, variantId) {
    const textSpan = label.querySelector('.variant-option__button-label__text');

    if (!textSpan) return;

    // Try to find variant price from product data
    let price = null;
    let compareAtPrice = null;

    // Use cached product data if available
    if (this.productData && this.productData.variants) {
      const variant = this.productData.variants.find(v => v.id === variantId);
      if (variant) {
        price = variant.price;
        compareAtPrice = variant.compare_at_price;
      }
    }

    // Display the price if found
    if (price !== null) {
      // Create price wrapper
      const priceWrapper = document.createElement('span');
      priceWrapper.className = 'variant-price';

      // Add compare at price if it exists and is higher than current price
      if (compareAtPrice && compareAtPrice > price) {
        const compareSpan = document.createElement('span');
        compareSpan.className = 'variant-price__compare';
        compareSpan.textContent = this.formatPrice(compareAtPrice);
        compareSpan.style.cssText = 'text-decoration: line-through; opacity: 0.6; margin-right: 0.25em;';
        priceWrapper.appendChild(compareSpan);
      }

      // Add current price
      const currentPriceSpan = document.createElement('span');
      currentPriceSpan.className = 'variant-price__current';
      currentPriceSpan.textContent = this.formatPrice(price);
      currentPriceSpan.style.cssText = 'font-weight: 600;';
      priceWrapper.appendChild(currentPriceSpan);

      // Style the wrapper
      priceWrapper.style.cssText = 'display: block; margin-top: 0.25em; font-size: 0.875em;';

      // Append to label instead of text span for better layout
      textSpan.appendChild(priceWrapper);
    }
  }
}

// Initialize when script loads
if (!window.variantPriceDisplayInitialized) {
  window.variantPriceDisplayInitialized = true;
  new VariantPriceDisplay();
}
